// Sentinel Mobile Agent shell (R-147 W6): the `/m/*` counterpart of
// ShellLayout — a slim header (screen title + connection/queue badges), the
// routed content and the BottomNav (Home / My Work / Scan / Profile). Selected
// by the Router on the /m path prefix; desktop routes keep ShellLayout.
import { type ComponentChildren } from 'preact';
import { Suspense } from 'preact/compat';
import * as stylex from '@stylexjs/stylex';
import '@tokens/colors.stylex';
import { colors } from '@tokens/colors.stylex';
import '@tokens/typography.stylex';
import { typography } from '@tokens/typography.stylex';
import { Badge, BottomNav, type BottomNavItem } from '@xantory/ui';
import { HomeIcon, ScanIcon, UserIcon, WrenchIcon } from '@xantory/icons';
import { useOfflineQueue, useOfflineQueueSync, useOnline } from '@xantory/api';
import { useT } from '@xantory/i18n';
import { ROUTES } from '@xantory/features';
import { RouteErrorBoundary } from './RouteErrorBoundary';
import { PageFallback } from './PageFallback';
interface MobileShellLayoutProps {
  /** The resolved route path (drives the title + the active tab). */
  activeRoute: string;
  children: ComponentChildren;
}
const TITLE_KEY = {
  [ROUTES.mHome]: 'navHome',
  [ROUTES.mWork]: 'navWork',
  [ROUTES.mScan]: 'navScan',
  [ROUTES.mProfile]: 'navProfile'
} as const;
export function MobileShellLayout({
  activeRoute,
  children
}: MobileShellLayoutProps) {
  const t = useT('technician');
  // R-145: one app-wide flush of the offline mutation queue (receiving, WO
  // complete, TO stage, incident notes) — reconnect + poll, invalidating each
  // flushed item's keys. Mounted once here so it runs on every authed route.
  // (Exactly one shell renders at a time — ShellLayout carries it on desktop.)
  useOfflineQueueSync();
  const online = useOnline();
  const queue = useOfflineQueue();
  const items: BottomNavItem[] = [{
    key: ROUTES.mHome,
    label: t('navHome'),
    icon: <HomeIcon />,
    href: ROUTES.mHome
  }, {
    key: ROUTES.mWork,
    label: t('navWork'),
    icon: <WrenchIcon />,
    href: ROUTES.mWork
  }, {
    key: ROUTES.mScan,
    label: t('navScan'),
    icon: <ScanIcon />,
    href: ROUTES.mScan
  }, {
    key: ROUTES.mProfile,
    label: t('navProfile'),
    icon: <UserIcon />,
    href: ROUTES.mProfile,
    badge: queue.length
  }];
  return <div className="xg6iff7 x78zum5 xdt5ytf x16vpd03">
      <header className="x78zum5 x6s0dn4 x883omv x10dr8xq x1hr3lfm xso031l x1q0q8m5 xra1o3">
        {/* Visual title only (a div, not a heading) — the routed page owns its
            own h1 (PageHeader on detail, the greeting on Home). */}
        <div className="x98rzlu x1ghz6dp xpc6l4x x1jvydc1 xomqrzn xjat59b x1oezkus">
          {t(TITLE_KEY[activeRoute as keyof typeof TITLE_KEY] ?? 'navHome')}
        </div>
        {!online && <Badge variant="warn">{t('offline')}</Badge>}
        {queue.length > 0 && <Badge variant="info">{t('queuedBadge', {
          count: queue.length
        })}</Badge>}
      </header>
      <main className="x98rzlu xyamay9 x1hr3lfm x1yqo8g1">
        {/* Keyed per-route host — same reconciler-anchoring rationale as ShellLayout. */}
        <div key={activeRoute} style={{
        display: 'contents'
      }}>
          <RouteErrorBoundary>
            <Suspense fallback={<PageFallback />}>{children}</Suspense>
          </RouteErrorBoundary>
        </div>
      </main>
      <BottomNav items={items} activeKey={activeRoute} label={t('bottomNavLabel')} />
    </div>;
}